/** Colores de familia de tarea (tarjetas en Tareas y botones de cualificación). */
import { getTaskScheduleWindow, isTcAgendaVisitTask } from "../utils/taskSchedule";

export const TASK_UI = {
  whatsapp: "#25D366",
  /** Naranja de mails (T1c por email cuando el teléfono no tiene WA). */
  email: "#f59e0b",
  llamada: "#ffDE21",
  cita: "#2563EB",
  captacion: "#f97316",
  sistema: "#94a3b8",
  espera: "#64748b",
  sinInteres: "#0ea5e9",
  noHecha: "#ff0000",
  textOnBright: "#000000",
  /** Mitad llamada / mitad WhatsApp (T4b1). */
  dualLlamadaWhatsappBg: "linear-gradient(to right, #ffDE21 50%, #25D366 50%)",
};

function parseTaskMetadata(task = {}) {
  if (!task?.metadata) return {};
  if (typeof task.metadata === "object") return task.metadata;
  try {
    return JSON.parse(task.metadata);
  } catch {
    return {};
  }
}

function mapRawTaskCodeToDisplay(raw) {
  const upper = String(raw || "").trim().toUpperCase();
  if (!upper) return null;
  if (upper === "TC") return "T4";
  if (upper === "T1C") return "T1c";
  if (upper === "T3B1") return "T3b1";
  if (upper === "T3B" || upper === "T2E") return "T3b";
  if (upper === "T4B1") return "T4b1";
  if (upper === "T4B2") return "T4b2";
  if (upper === "T4B3") return "T4b3";
  if (upper === "T3BN") return "T3bN";
  if (upper === "T4BN") return "T4bN";
  if (upper === "T2B") return "T2b";
  if (upper === "T1B") return "T1b";
  if (upper === "T1R") return "T1";
  return upper;
}

/** T2A (M1): en UI muestra T1/T1b/T1c según el contacto que originó la cualificación. */
function resolveQualificationOriginCode(meta = {}) {
  const prev = String(meta.qualification_source_code || meta.previous_task_code || "").trim().toUpperCase();
  const directMap = {
    T1: "T1",
    T1R: "T1",
    T1B: "T1B",
    T1C: "T1C",
    T2L: "T1B",
  };
  if (directMap[prev]) return directMap[prev];

  const byOrigin = {
    call_first: "T1",
    call_second: "T1B",
    whatsapp: "T1C",
    call_retry: "T1B",
    callback: "T1",
  };
  const origin = String(meta.contact_origin || "").toLowerCase();
  if (byOrigin[origin]) return byOrigin[origin];

  if (meta.m1_trigger === "visit_cancelled" || origin === "visit_rebooking") return "T4";

  return "T1";
}

function getQualificationTaskDisplayCode(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  return mapRawTaskCodeToDisplay(resolveQualificationOriginCode(meta));
}

/** Tareas de captación/precaptación (legacy o flujo T). */
export function isCaptacionTask(tarea = {}) {
  const tipo = String(tarea?.tipo || "").toLowerCase();
  return Boolean(tarea?.captacion_id) || tipo === "captacion";
}

/** Flujo T1/T1b/T1c/T4 de captación (misma nomenclatura que leads). */
export function isCaptacionFlowTask(tarea = {}) {
  if (!isCaptacionTask(tarea)) return false;
  const meta = parseTaskMetadata(tarea);
  return meta.flow_id === "captacion_incoming_v1" || meta.captacion_flow === true;
}

/** Título visible para captaciones legacy (sin prefijo T). */
function resolveCaptacionTaskTitle(tarea = {}, fallbackLabel = "") {
  const titulo = String(tarea?.titulo || "").trim();
  const stripped = titulo.replace(/^T\d+[a-z]?\d?\s*[·.\-–]\s*/i, "").trim();
  if (stripped) return stripped;

  const desc = String(tarea?.descripcion || "").trim();
  if (/^seguimiento\s+captaci[oó]n/i.test(desc)) return "Seguimiento captación";
  if (/^contactar\s+con/i.test(desc)) return "Contactar";
  if (desc) {
    const antesComa = desc.split(",")[0].trim();
    if (/aviso/i.test(antesComa)) return antesComa;
    const antesDosPuntos = desc.split(":")[0].trim();
    if (antesDosPuntos) return antesDosPuntos;
  }

  const tipo = String(tarea?.tipo || "").toLowerCase();
  if (tipo === "seguimiento") return "Seguimiento captación";
  if (fallbackLabel) return fallbackLabel;
  return "Captación";
}

function inferWhatsappWaitCode(meta = {}) {
  if (meta.t1c_whatsapp_sent_at) return "T1C";
  if (meta.t3b1_whatsapp_sent_at) return "T3B1";
  if (meta.t3b_whatsapp_sent_at) return "T3B";
  if (meta.t2e_whatsapp_sent_at) return "T2E";
  if (meta.t4b1_whatsapp_sent_at || meta.t4b1_call_at) return "T4B1";
  if (meta.t4b2_whatsapp_sent_at) return "T4B2";
  if (meta.t4b3_whatsapp_sent_at) return "T4B3";
  return "";
}

/** Inferir código T cuando metadata o título vienen sin nomenclatura (p. ej. solo «Visita»). */
function inferTaskCodeFromContext(tarea = {}, meta = {}) {
  // Legacy captación sin flujo T: sin código. Flujo captación: igual que leads.
  if (isCaptacionTask(tarea) && !isCaptacionFlowTask(tarea)) return "";

  if (meta.requires_visit_feedback_outcome === true) return "T4";
  if (meta.requires_visit_confirm_outcome === true) {
    const label = String(tarea?.titulo || meta.task_label || "").toLowerCase();
    if (/tras env[ií]o autom[aá]tico|t3ae/i.test(label)) return "T3AE";
    return "T3";
  }
  if (meta.agenda_id && (tarea?.lead_id || isCaptacionFlowTask(tarea))) return "TC";

  if (meta.flow_substate === "esperar_respuesta") {
    const waitCode = inferWhatsappWaitCode(meta);
    if (waitCode) return waitCode;
  }

  const titulo = String(tarea?.titulo || "").trim();
  const tipo = String(tarea?.tipo || "").toLowerCase();
  const hasLead = Boolean(tarea?.lead_id);
  const isCaptacion = Boolean(tarea?.captacion_id);

  if (/^cualificar$/i.test(titulo) && String(meta.task_code || "").toUpperCase() === "T2A") {
    return resolveQualificationOriginCode(meta);
  }

  if (/^cualificar cita$/i.test(titulo)) return "T4";
  if (/^agendar cita$/i.test(titulo)) return "T3";
  if (/^visita$/i.test(titulo) || (tipo === "visita" && hasLead && !isCaptacion)) {
    return meta.agenda_id ? "TC" : "T4";
  }
  if (/^seguimiento$/i.test(titulo) || (tipo === "seguimiento" && hasLead && !isCaptacion)) return "T2B";
  if (/^llamada inicial$/i.test(titulo)) return "T1";

  if (/esperar respuesta/i.test(titulo)) {
    const waitCode = inferWhatsappWaitCode(meta);
    if (waitCode) return waitCode;
  }
  if (/^evaluar respuesta$/i.test(titulo)) {
    const waitCode = inferWhatsappWaitCode(meta);
    if (waitCode && waitCode !== "T3B1") return waitCode;
    return "T3B";
  }

  if (hasLead && !isCaptacion) {
    const byTipo = {
      visita: meta.agenda_id ? "TC" : "T4",
      agendar_visita: "T3",
      seguimiento: "T2B",
      seguimiento_ia: "T3",
      contactar_ia: "T1",
      enviar_propiedades: "T2E",
      whatsapp: "T3B",
      contactar: "T4B1",
      segunda_llamada: "T1B",
      llamar: meta.flow_owner === "ana" || meta.owner === "ana" ? "T1" : "",
    };
    if (byTipo[tipo]) return byTipo[tipo];
    if (tipo === "llamar" && /volver a llamar/i.test(String(tarea?.descripcion || ""))) return "T2L";
  }

  return "";
}

function extractCodeFromTitle(text = "") {
  const match = String(text || "").trim().match(/^(T\d+[a-z]?\d?)\s*[·.\-–]/i);
  return match ? mapRawTaskCodeToDisplay(match[1]) : null;
}

/** Código interno en mayúsculas (T4B1, T3B…) desde metadata o título. */
function taskWithFallbackTitle(tarea = {}, fallbackLabel = "") {
  const titulo = String(tarea?.titulo || "").trim();
  if (titulo || !fallbackLabel) return tarea;
  return { ...tarea, titulo: String(fallbackLabel).trim() };
}

export function resolveTaskCodeUpper(tarea = {}, fallbackLabel = "") {
  const effective = taskWithFallbackTitle(tarea, fallbackLabel);
  // Legacy captación: sin código T. Flujo captación: sí.
  if (isCaptacionTask(effective) && !isCaptacionFlowTask(effective)) return "";

  const meta = parseTaskMetadata(effective);
  const raw = String(meta.task_code || "").trim().toUpperCase();
  if (raw) return raw;

  const match = String(meta.task_label || effective.titulo || "").trim().match(/^(T\d+[a-z]?\d?)\s*[·.\-–]/i);
  if (match) return String(match[1]).toUpperCase();

  if (/llamada o whatsapp/i.test(String(effective.titulo || meta.task_label || ""))) return "T4B1";

  const inferred = inferTaskCodeFromContext(effective, meta);
  if (inferred) return inferred;

  return "";
}

export function isT4b1DualChannelTask(tarea = {}) {
  return resolveTaskCodeUpper(tarea) === "T4B1";
}

/** Código visible en UI (p. ej. TC interno → T4; T2E legacy → T3b). */
export function getTaskDisplayCode(tarea = {}, fallbackLabel = "") {
  const effective = taskWithFallbackTitle(tarea, fallbackLabel);
  const meta = parseTaskMetadata(effective);
  if (String(meta.task_code || "").trim().toUpperCase() === "T2A") {
    const qualCode = getQualificationTaskDisplayCode(effective);
    if (qualCode) return qualCode;
  }

  const raw = resolveTaskCodeUpper(effective, fallbackLabel);
  if (raw) return mapRawTaskCodeToDisplay(raw);

  for (const source of [meta.task_label, effective.titulo]) {
    const fromTitle = extractCodeFromTitle(source);
    if (fromTitle) return fromTitle;
  }
  return null;
}

/** Título visible con nomenclatura (T1, T4, T1c…) integrada en el texto, sin badge aparte. */
export function formatTaskTitleWithCode(tarea = {}, fallbackLabel = "") {
  const effective = taskWithFallbackTitle(tarea, fallbackLabel);
  if (isCaptacionTask(effective) && !isCaptacionFlowTask(effective)) {
    return resolveCaptacionTaskTitle(effective, fallbackLabel);
  }

  const meta = parseTaskMetadata(effective);
  const taskCode = resolveTaskCodeUpper(effective, fallbackLabel);
  if (taskCode === "T2A") {
    const code = getQualificationTaskDisplayCode(effective) || "T1";
    return `${code} · Cualificar`;
  }

  const code = getTaskDisplayCode(effective, fallbackLabel);
  // T4b2/T4b3: mismo código; el título refleja si aún hay que enviar o ya se espera respuesta.
  if (taskCode === "T4B2" || taskCode === "T4B3") {
    const disp = code || (taskCode === "T4B3" ? "T4b3" : "T4b2");
    const replyPending =
      meta.whatsapp_reply_pending === true ||
      meta.whatsapp_reply_pending === "true" ||
      Boolean(meta.whatsapp_inbound_received_at);
    if (replyPending) return `${disp} · Evaluar respuesta`;
    if (isWhatsappWaitingState(effective)) return `${disp} · Esperar respuesta`;
    return taskCode === "T4B3" ? `${disp} · Enviar propiedad` : `${disp} · Enviar WhatsApp`;
  }
  if (taskCode === "T3BN") {
    return `${code || "T3bN"} · Buscar propiedad`;
  }
  if (taskCode === "T4BN") {
    return `${code || "T4bN"} · Buscar propiedad`;
  }

  let base = String(effective.titulo || meta.task_label || fallbackLabel || "").trim();
  base = base.replace(/responder y\s+cualificar/gi, "Cualificar");
  if (!code) return base;
  if (!base) return code;

  const yaTieneCodigo = (codigo) => {
    if (!codigo) return false;
    const esc = String(codigo).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`^${esc}\\s*[·.\\-–]`, "i").test(base);
  };

  if (/^evaluar respuesta$/i.test(base)) return `${code} · Evaluar respuesta`;
  if (/esperar respuesta/i.test(base)) return `${code} · Esperar respuesta`;

  if (yaTieneCodigo(code)) return base;

  const raw = taskCode || String(meta.task_code || "").trim().toUpperCase();
  if (raw && yaTieneCodigo(raw)) {
    if (raw !== code.toUpperCase()) {
      return base.replace(new RegExp(`^${raw}`, "i"), code);
    }
    return base;
  }

  if (/^T2E\s*[·.\-–]/i.test(base) && code === "T3b") {
    return base.replace(/^T2E\s*[·.\-–]\s*/i, `${code} · `);
  }

  return `${code} · ${base}`;
}

/** Fecha/hora compacta para tarjetas del pipeline de tareas. */
export function formatTareaCardFecha(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Madrid",
  });
}

/** Etiqueta + nombre del contacto en tarjeta (lead, cliente o captación). */
export function getTareaContactoEtiqueta(tarea = {}) {
  const lead = String(tarea.lead_nombre || "").trim();
  if (lead) return { label: "Lead de:", nombre: lead };
  const cliente = String(tarea.cliente_nombre || "").trim();
  if (cliente) return { label: "Cliente:", nombre: cliente };
  const captacion = String(tarea.captacion_nombre || "").trim();
  if (captacion) return { label: "Captación de:", nombre: captacion };
  return null;
}

/** Referencia de propiedad visible en tarjeta (lead, metadata, cliente o cita). */
export function resolveTareaPropiedadRef(tarea = {}) {
  const refs = resolveTareaPropiedadRefs(tarea);
  return refs[0] || null;
}

/**
 * Todas las refs asociadas a la tarea.
 * Lead de entrada → ref de entrada; cita → refs de la agenda/cita (varias).
 */
export function resolveTareaPropiedadRefs(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  const out = [];
  const push = (raw) => {
    const s = String(raw || "").trim();
    if (s && !out.includes(s)) out.push(s);
  };

  const fromApi = tarea.propiedad_refs;
  if (Array.isArray(fromApi)) fromApi.forEach(push);
  else if (typeof fromApi === "string" && fromApi.trim()) {
    fromApi.split(/[,;]+/).forEach(push);
  }

  if (Array.isArray(meta.propiedad_refs)) meta.propiedad_refs.forEach(push);

  const taskCode = String(meta.task_code || "").toUpperCase();
  if (meta.agenda_id && ["TC", "T4"].includes(taskCode)) {
    push(meta.propiedad_ref);
  }

  push(tarea.propiedad_ref);
  push(meta.propiedad_ref);
  push(meta.referencia);
  return out;
}

/** Inicio de ventana de ejecución (fecha_inicio efectiva). */
export function getTareaEntradaIso(tarea = {}) {
  const { inicio } = getTaskScheduleWindow(tarea);
  if (inicio && !Number.isNaN(inicio.getTime())) return inicio.toISOString();
  return tarea.created_at || tarea.lead_created_at || tarea.captacion_created_at || null;
}

/** Acción legible para la tarjeta (sin nombre del agente ni descripción interna). */
export function formatTaskActionLabel(tarea = {}, fallbackLabel = "") {
  const meta = parseTaskMetadata(tarea);
  const taskCode = String(meta.task_code || "").trim().toUpperCase();
  if (taskCode === "T4B2" || taskCode === "T4B3") {
    const replyPending =
      meta.whatsapp_reply_pending === true ||
      meta.whatsapp_reply_pending === "true" ||
      Boolean(meta.whatsapp_inbound_received_at);
    if (replyPending) return "Evaluar respuesta del cliente";
    if (isWhatsappWaitingState(tarea)) return "Esperar respuesta del lead";
    return taskCode === "T4B3"
      ? "Enviar propiedad por WhatsApp o mail"
      : "Enviar WhatsApp al cliente";
  }

  let base = String(tarea?.titulo || meta.task_label || fallbackLabel || "").trim();
  base = base.replace(/^responder y\s+cualificar$/i, "Cualificar");
  base = base.replace(/responder y\s+cualificar/gi, "Cualificar");
  base = base.replace(/^T\d+[a-zA-Z]?\d?\s*[·.\-–]\s*/i, "").trim();

  if (/^evaluar respuesta$/i.test(base)) return "Evaluar respuesta del cliente";
  if (/esperar respuesta/i.test(base)) return "Esperar respuesta del lead";
  if (/^no hecha$/i.test(base)) return "Completar tarea no realizada";
  if (!base) return "Completar tarea";

  const lower = base.charAt(0).toLowerCase() + base.slice(1);
  if (/^(agendar|enviar|evaluar|esperar|confirmar|cualificar|registrar|llamar|contactar|revisar|seguir)/i.test(lower)) {
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }
  return `Realizar ${lower}`;
}

export function isWhatsappWaitingState(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  const code = String(meta.task_code || "").toUpperCase();
  const titulo = String(tarea?.titulo || "");

  if (meta.flow_substate === "esperar_respuesta") return true;
  if (/esperar respuesta/i.test(titulo)) return true;

  if (code === "T1C" && (meta.t1c_whatsapp_sent_at || meta.t1c_email_sent_at)) return true;
  if (code === "T2E" && (meta.t2e_whatsapp_sent_at || meta.t3b_whatsapp_sent_at)) return true;
  if (code === "T3B" && meta.t3b_whatsapp_sent_at) return true;
  if (code === "T3B1" && meta.t3b1_whatsapp_sent_at) return true;
  if (
    code === "T4" &&
    (meta.t4_post_visita_espera === true ||
      meta.t4_post_visita_espera === "true" ||
      meta.t4_espera_started_at ||
      (meta.flow_substate === "esperar_respuesta" &&
        (meta.t4_waiting_response === true || meta.t4_waiting_response === "true")))
  ) {
    return true;
  }
  if (code === "T4B1" && (meta.t4b1_whatsapp_sent_at || meta.t4b1_call_at)) return true;
  if (code === "T4B2" && meta.t4b2_whatsapp_sent_at) return true;
  if (code === "T4B3" && meta.t4b3_whatsapp_sent_at) return true;
  return false;
}

/** Familia visual de la tarea (misma lógica que la hoja Tareas). */
export function getTaskCategoryKey(tarea = {}) {
  const tipo = String(tarea.tipo || "").toLowerCase();
  const meta = parseTaskMetadata(tarea);
  const code = resolveTaskCodeUpper(tarea);
  const canal = String(tarea.canal || "").toLowerCase();

  // T4b1 en espera de respuesta: gris (como el resto de WA wait), no dual.
  // Dual amarillo/verde solo mientras toca llamar o mandar el WhatsApp.
  if (isWhatsappWaitingState(tarea)) return "espera";

  // T4b1 = llamar O WhatsApp (aún no enviado / no en espera).
  if (code === "T4B1") return "llamada_whatsapp";

  // Flujo T de captación (T1/T4…): familia visual naranja + icono casa.
  if (isCaptacionFlowTask(tarea)) {
    return "captacion";
  }

  // Manuales de captación: mismos colores/iconos que leads (WA verde, llamada amarilla, cita azul).
  if (tarea.captacion_id || tipo === "captacion") {
    if (
      tipo === "whatsapp" ||
      canal === "whatsapp" ||
      tipo === "enviar_propiedades"
    ) return "whatsapp";
    if (tipo === "email" || canal === "email") return "email";
    if (
      tipo === "llamar" ||
      tipo === "segunda_llamada" ||
      tipo === "llamada" ||
      tipo === "contactar" ||
      tipo === "revisar_llamada" ||
      canal === "llamada"
    ) return "llamada";
    if (tipo === "visita" || tipo === "agendar_visita") return "cita";
    return "captacion";
  }

  // T1c por email (sin WA en el teléfono): naranja de mails, no verde WA.
  if (
    code === "T1C" &&
    (String(meta.t1c_channel || "").toLowerCase() === "email" ||
      tipo === "email" ||
      String(meta.canal_contacto || "").toLowerCase() === "email")
  ) {
    return "email";
  }

  if (
    tipo === "email" ||
    canal === "email"
  ) return "email";

  if (
    tipo === "whatsapp" ||
    canal === "whatsapp" ||
    code === "T1C" ||
    code === "T2E" ||
    code === "T3B" ||
    code === "T3B1" ||
    code === "T4B2" ||
    code === "T4B3" ||
    tipo === "enviar_propiedades"
  ) return "whatsapp";

  if (
    tipo === "visita" ||
    tipo === "agendar_visita" ||
    tipo === "cierre" ||
    code === "TC" ||
    code === "T3" ||
    code === "T4" ||
    code === "T7"
  ) return "cita";

  if (
    tipo === "llamar" ||
    tipo === "segunda_llamada" ||
    tipo === "llamada" ||
    tipo === "contactar" ||
    tipo === "revisar_llamada" ||
    code === "T1" ||
    code === "T1B" ||
    code === "T2L" ||
    canal === "llamada"
  ) return "llamada";

  if (code === "T2A") {
    const origin = String(meta.contact_origin || "").toLowerCase();
    if (origin === "whatsapp") return "whatsapp";
    return "llamada";
  }

  return "sistema";
}

/** Tarea creada a mano por el usuario (modal captación), editable. */
export function isTareaManualUsuario(tarea = {}) {
  const origen = String(tarea?.origen || "").toLowerCase();
  const meta = parseTaskMetadata(tarea);
  const source = String(meta.source || "").toLowerCase();
  if (origen.includes("manual_captacion")) return true;
  if (source.includes("modal_crear_tarea_captacion")) return true;
  return false;
}

/**
 * Batería de plazo solo en tareas automáticas del embudo de leads
 * (T1/T2/T3/T4/M…), no captaciones ni recordatorios manuales.
 * TC · Cita: no lleva batería (el «plazo» es la hora de la visita, no horas laborables).
 */
export function debeMostrarPlazoBateria(tarea = {}) {
  if (!tarea?.lead_id) return false;
  if (isCaptacionTask(tarea)) return false;
  if (isTareaManualUsuario(tarea)) return false;
  const meta = parseTaskMetadata(tarea);
  const code = String(meta.task_code || "").trim().toUpperCase();
  if (code === "TC") return false;
  const flowId = String(meta.flow_id || "").trim();
  if (flowId && flowId !== "captacion_incoming_v1") {
    // Misma exclusión si el flow_id aplica pero el código es cita.
    return code !== "TC";
  }
  if (/^(T\d|M\d)/.test(code)) return true;
  return false;
}

/** Categorías usadas en filtros (puede haber más de una por tarea). */
export function getTaskFilterCategories(tarea = {}) {
  const key = getTaskCategoryKey(tarea);
  if (key === "espera") return ["espera", "sistema"];
  if (key === "llamada_whatsapp") return ["llamada", "whatsapp"];
  // Captación: también por canal/tipo para que no desaparezca si el filtro activo es WhatsApp/Llamada/Cita
  if (key === "captacion") {
    const tipo = String(tarea.tipo || "").toLowerCase();
    const cats = ["captacion"];
    if (tipo === "whatsapp") cats.push("whatsapp");
    if (["llamar", "llamada", "contactar", "segunda_llamada"].includes(tipo)) cats.push("llamada");
    if (["visita", "agendar_visita"].includes(tipo)) cats.push("cita");
    if (tipo === "seguimiento") cats.push("sistema");
    return cats;
  }
  // Manuales con color de canal: siguen visibles en filtro Captación
  if (tarea.captacion_id && !tarea.lead_id && ["whatsapp", "llamada", "cita", "sistema"].includes(key)) {
    return [key, "captacion"];
  }
  return [key];
}

/** Etiqueta de una palabra para la esquina derecha de «Tarea vigente». */
export function getTaskTypeLabel(categoryKey) {
  const labels = {
    whatsapp: "WhatsApp",
    email: "Email",
    llamada: "Llamada",
    llamada_whatsapp: "Llamada / WA",
    cita: "Cita",
    captacion: "Captación",
    sistema: "Sistema",
    espera: "Espera",
  };
  return labels[categoryKey] || "Tarea";
}

/** Fondo y texto de la caja «Tarea vigente» en el modal del lead / ficha cliente. */
export function isTaskVigenteExpired(tarea, { leadEtapa = null } = {}) {
  const meta = parseTaskMetadata(tarea);
  // Solo «No hecha» congelada (Fríos). No pintar rojo por etapa Fríos/Eliminados:
  // al coger un frío y mandar WA la etapa/badge aún puede decir frios un momento
  // con una tarea nueva → flash rojo incorrecto (Test1 / Sabrina).
  if (meta.display_unfinished === true || meta.display_unfinished === "true") return true;
  void leadEtapa;
  // TC · Cita: el due es el fin de la visita → pasa a T4/M2, no «No hecha» en rojo
  // (misma regla que isLeadTaskExpired / isTareaPlazoVencido). Regresión: 2ª cita Test1.
  if (isTcAgendaVisitTask(tarea)) return false;
  if (!tarea?.fecha_vencimiento) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado === "completada" || estado === "cancelada") return false;
  const due = new Date(tarea.fecha_vencimiento);
  return !Number.isNaN(due.getTime()) && due.getTime() < Date.now();
}

export function getTaskVigenteLeadModalStyle(tarea, options = {}) {
  if (isTaskVigenteExpired(tarea, options)) {
    return {
      bg: TASK_UI.noHecha,
      text: "#ffffff",
      codeBg: "rgba(255,255,255,0.22)",
      key: "vencida",
    };
  }

  const key = tarea ? getTaskCategoryKey(tarea) : "sistema";
  const bgByKey = {
    whatsapp: TASK_UI.whatsapp,
    email: TASK_UI.email,
    llamada: TASK_UI.llamada,
    llamada_whatsapp: TASK_UI.dualLlamadaWhatsappBg,
    cita: TASK_UI.cita,
    captacion: TASK_UI.captacion,
    sistema: TASK_UI.sistema,
    espera: TASK_UI.espera,
  };
  const bg = bgByKey[key] || TASK_UI.sistema;
  const text = key === "cita" || key === "espera" ? "#ffffff" : "#000000";
  const codeBg = key === "cita" || key === "espera" ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.12)";
  return { bg, text, codeBg, key };
}
